import { useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { Bookmark, ChartGantt, Columns3, List, Plus, Trash2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { HEALTH_LABEL, isOverdue, useTracker, type Task, type TrackerData } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { cn, fmtDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/field';
import { Kbd, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';
import { HealthDot } from '@/components/tracker/bits';
import { useQuickAdd } from '@/components/tracker/QuickAdd';
import { Roadmap } from './Roadmap';
import { Board } from './Board';
import { ListView } from './ListView';

type View = 'roadmap' | 'board' | 'list';
const VIEWS: Array<{ v: View; label: string; icon: typeof List }> = [
  { v: 'roadmap', label: 'Roadmap', icon: ChartGantt },
  { v: 'board', label: 'Board', icon: Columns3 },
  { v: 'list', label: 'List', icon: List },
];

/** Monday to Sunday containing `today`. */
function weekOf(today: string) {
  const d = new Date(`${today}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  const mon = new Date(d.getTime() - dow * 86400000);
  const sun = new Date(mon.getTime() + 6 * 86400000);
  return [mon.toISOString().slice(0, 10), sun.toISOString().slice(0, 10)] as const;
}

export function applyFilters(tasks: Task[], data: TrackerData, f: URLSearchParams) {
  const phase = f.get('phase');
  const ws = f.get('ws');
  const owner = f.get('owner');
  const due = f.get('due');
  const [mon, sun] = weekOf(data.today);
  return tasks.filter(
    (t) =>
      (!phase || t.phaseId === phase) &&
      (!ws || t.workstreamId === ws) &&
      (!owner || (owner === 'none' ? !t.ownerId : t.ownerId === owner)) &&
      (!due ||
        (due === 'week' && t.status !== 'DONE' && !!t.dueDate && t.dueDate >= mon && t.dueDate <= sun) ||
        (due === 'overdue' && isOverdue(t, data.today))),
  );
}

export function TrackerPage() {
  const program = useOutletContext<Program>();
  const { data: me } = useMe();
  const tracker = useTracker(program.id);
  const [params, setParams] = useSearchParams();
  const quickAdd = useQuickAdd();
  const view = (params.get('view') as View) || 'roadmap';
  const isAdmin = program.capabilities.includes('plan.manage');
  const myId = me?.user.id;
  const isContributor = program.membership.role === 'CONTRIBUTOR';

  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k === 'phase') next.delete('ws');
    setParams(next, { replace: true });
  };

  const sorting: SortingState = useMemo(() => {
    const s = params.get('sort');
    return s ? [{ id: s.replace(/^-/, ''), desc: s.startsWith('-') }] : [];
  }, [params]);

  if (tracker.isLoading || !tracker.data) return <Spinner className="h-full" />;
  const data = tracker.data;
  const tasks = applyFilters(data.tasks, data, params);
  const phase = data.phases.find((p) => p.id === params.get('phase'));
  const canEditProgress = (t: Task) => isAdmin || (isContributor && t.ownerId === myId);
  const filtersActive = ['phase', 'ws', 'owner', 'due'].some((k) => params.get(k));

  return (
    <div className="mx-auto max-w-[1500px] px-6 py-5">
      {/* Program strip */}
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <h1 className="text-xl font-semibold tracking-tight">Tracker</h1>
        <span className="flex items-center gap-2 text-sm">
          <HealthDot level={data.health} />
          <span className="font-medium">Program {HEALTH_LABEL[data.health].toLowerCase()}</span>
          <span className="text-muted">
            · {data.phasesInFlight} phase{data.phasesInFlight === 1 ? '' : 's'} in flight · today {fmtDate(data.today)}
          </span>
        </span>
        <span className="text-sm text-muted">
          PA §4 deliverables:{' '}
          <span className="font-medium text-text tabular">
            {data.paProgress.done} of {data.paProgress.total}
          </span>{' '}
          accepted
        </span>
        {isAdmin && (
          <Button size="sm" className="ml-auto" onClick={quickAdd}>
            <Plus /> Task <Kbd>N</Kbd>
          </Button>
        )}
      </div>

      {/* View switch and filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div
          className="flex rounded-md border border-border-strong bg-surface p-0.5"
          role="tablist"
          aria-label="Tracker view"
        >
          {VIEWS.map((x) => (
            <button
              key={x.v}
              role="tab"
              aria-selected={view === x.v}
              onClick={() => set('view', x.v === 'roadmap' ? null : x.v)}
              className={cn(
                'flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px]',
                view === x.v ? 'bg-surface-3 font-medium text-text' : 'text-muted hover:text-text',
              )}
            >
              <x.icon className="size-4" /> {x.label}
            </button>
          ))}
        </div>
        {view !== 'roadmap' && (
          <>
            <Select
              aria-label="Phase"
              className="h-8 w-44 text-[13px]"
              value={params.get('phase') ?? ''}
              onChange={(e) => set('phase', e.target.value || null)}
            >
              <option value="">All phases</option>
              {data.phases.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number}. {p.name}
                </option>
              ))}
            </Select>
            {phase && phase.workstreams.length > 0 && (
              <Select
                aria-label="Workstream"
                className="h-8 w-40 text-[13px]"
                value={params.get('ws') ?? ''}
                onChange={(e) => set('ws', e.target.value || null)}
              >
                <option value="">All workstreams</option>
                {phase.workstreams.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            )}
            <Select
              aria-label="Owner"
              className="h-8 w-40 text-[13px]"
              value={params.get('owner') ?? ''}
              onChange={(e) => set('owner', e.target.value || null)}
            >
              <option value="">Anyone</option>
              {myId && <option value={myId}>Me</option>}
              <option value="none">Unassigned</option>
              {data.members
                .filter((m) => m.userId !== myId)
                .map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name}
                  </option>
                ))}
            </Select>
            <Select
              aria-label="Due"
              className="h-8 w-36 text-[13px]"
              value={params.get('due') ?? ''}
              onChange={(e) => set('due', e.target.value || null)}
            >
              <option value="">Any due date</option>
              <option value="week">Due this week</option>
              <option value="overdue">Overdue</option>
            </Select>
            {view === 'board' && (
              <label className="flex items-center gap-1.5 text-[13px] text-muted">
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--brand)]"
                  checked={params.get('swim') === '1'}
                  onChange={(e) => set('swim', e.target.checked ? '1' : null)}
                />
                Swimlanes by phase
              </label>
            )}
            {filtersActive && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  for (const k of ['phase', 'ws', 'owner', 'due']) next.delete(k);
                  setParams(next, { replace: true });
                }}
              >
                Clear filters
              </Button>
            )}
            <SavedViews programId={program.id} surface={view === 'board' ? 'board' : 'list'} />
            <span className="ml-auto text-xs text-muted tabular">
              {tasks.length} of {data.tasks.length} tasks
            </span>
          </>
        )}
      </div>

      {view === 'roadmap' && <Roadmap program={program} data={data} canEdit={isAdmin} />}
      {view === 'board' && (
        <Board
          programId={program.id}
          data={data}
          tasks={tasks}
          canDrag={canEditProgress}
          swimlanes={params.get('swim') === '1'}
        />
      )}
      {view === 'list' && (
        <ListView
          programId={program.id}
          data={data}
          tasks={tasks}
          isAdmin={isAdmin}
          canEditProgress={canEditProgress}
          sorting={sorting}
          onSortingChange={(s) => set('sort', s[0] ? `${s[0].desc ? '-' : ''}${s[0].id}` : null)}
        />
      )}
    </div>
  );
}

const VIEW_KEYS = ['phase', 'ws', 'owner', 'due', 'sort', 'swim'];

/** Saved filters and sort, per person, stored on the server so they follow you. */
function SavedViews({ programId, surface }: { programId: string; surface: 'list' | 'board' }) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['views', programId, surface];
  const views = useQuery({
    queryKey: key,
    queryFn: () =>
      api.get<Array<{ id: string; name: string; filters: Record<string, string> }>>(
        `/api/programs/${programId}/views?surface=${surface}`,
      ),
  });
  const save = useMutation({
    mutationFn: (name: string) =>
      api.post(`/api/programs/${programId}/views`, {
        name,
        surface,
        filters: Object.fromEntries(VIEW_KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)!])),
      }),
    onSuccess: () => {
      toast.ok('View saved.');
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/programs/${programId}/views/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const apply = (filters: Record<string, string>) => {
    const next = new URLSearchParams();
    if (params.get('view')) next.set('view', params.get('view')!);
    for (const [k, v] of Object.entries(filters)) next.set(k, v);
    setParams(next, { replace: true });
  };

  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const current = views.data?.find((v) => VIEW_KEYS.every((k) => (v.filters[k] ?? null) === params.get(k)));

  if (naming) {
    return (
      <form
        className="flex items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate(name.trim(), { onSuccess: () => setNaming(false) });
        }}
      >
        <Input
          autoFocus
          aria-label="View name"
          placeholder="Name this view"
          className="h-8 w-40 text-[13px]"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && setNaming(false)}
        />
        <Button type="submit" size="sm" loading={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setNaming(false)}>
          Cancel
        </Button>
      </form>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Select
        aria-label="Saved views"
        className="h-8 w-36 text-[13px]"
        value={current?.id ?? ''}
        onChange={(e) => {
          const v = views.data?.find((x) => x.id === e.target.value);
          if (v) apply(v.filters);
        }}
      >
        <option value="">Saved views…</option>
        {views.data?.map((v) => (
          <option key={v.id} value={v.id}>
            {v.name}
          </option>
        ))}
      </Select>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Save current filters as a view"
        title="Save these filters as a view"
        onClick={() => {
          setName(current?.name ?? '');
          setNaming(true);
        }}
      >
        <Bookmark />
      </Button>
      {current && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Delete the view ${current.name}`}
          title={`Delete "${current.name}"`}
          onClick={() => remove.mutate(current.id)}
        >
          <Trash2 />
        </Button>
      )}
    </div>
  );
}
